import React, { useState, useEffect } from 'react';
import { MasterMessage } from '../../../masters/masterMessages';
import ListView from '../../components/views/listView';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { HOME } from '../../../routes/paths';
import { useNavigate } from 'react-router-dom';
import { Grid } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import DatePickerField from '../../components/general/fields/datePickerField';
import dayjs from 'dayjs';
import MultipleAutocompleteField from '../../components/general/fields/multipleAutocompleteField';
import { asyncGetUserByRol } from '../../../services/caseManager.service';
import { SwalCustomClass } from '../../components/general/toastSwal/swalCustomClass';
import {
    asyncPostActivityTimesExport,
    asyncPostDataActivityTimesExport,
} from '../../../services/activityTimes.service';
import { IActivityTimeExportFile } from '../../../shared/types/activityTimes';
import { downloadFileFromUrl } from '../../../shared/utils/files';

const ExportActivityTimes = () => {
    const navigate = useNavigate();
    const [rows, setRows] = useState<any>();
    const [page, setPage] = useState<number>(0);
    const [lengthRows, setLengthRows] = useState<number>(0);
    const [pageSize, setPageSize] = useState<number>(20);
    const [users, setUsers] = useState<any>();
    const [startTime, setStartTime] = useState<any>(null);
    const { titleList, messageOk, messageError } =
        MasterMessage.exportActivityTimes;

    const formik = useFormik({
        initialValues: {
            startTime: null,
            endTime: null,
            userId: [],
        },
        validationSchema: Yup.object({
            startTime: Yup.string().required('La fecha inicial es obligatoria'),
            endTime: Yup.string()
                .nullable()
                .when(['startTime'], {
                    is: (startTime: any) => Boolean(startTime !== null),
                    then: (schema) =>
                        schema.required('La fecha final es obligatoria'),
                }),
        }),
        onSubmit: async (exportActivityTimes: any) => {
            exportActivityTimes.endTime = dayjs(
                exportActivityTimes.endTime
            ).endOf('day');
            const body: any = await asyncPostActivityTimesExport(
                exportActivityTimes
            );
            const file = body?.items as IActivityTimeExportFile | undefined;
            if (file?.url && file?.name) {
                await downloadFileFromUrl(file.name, file.url);
                SwalCustomClass.fire({
                    text: messageOk,
                    icon: 'success',
                    iconColor: '#1d1d1d',
                });
            } else {
                SwalCustomClass.fire({
                    text: messageError,
                    icon: 'error',
                    iconColor: '#1d1d1d',
                });
            }
        },
    });

    const paginator = (paginationModel: any) => {
        if (paginationModel.page !== page) {
            setPage(paginationModel.page);
        } else if (paginationModel.pageSize !== pageSize) {
            setPageSize(paginationModel.pageSize);
        }
    };

    const columns = [
        {
            field: 'Codigo',
            headerName: 'Codigo',
            minWidth: 100,
            flex: 0.5,
        },
        {
            field: 'Estado',
            headerName: 'Estado',
            minWidth: 120,
            flex: 1,
        },
        {
            field: 'Fin',
            headerName: 'Fin',
            minWidth: 120,
            flex: 1,
        },
        {
            field: 'Inicio',
            headerName: 'Inicio',
            minWidth: 120,
            flex: 1,
        },
        {
            field: 'Ip',
            headerName: 'Ip',
            minWidth: 120,
            flex: 1,
        },
        {
            field: 'Latitud',
            headerName: 'Latitud',
            minWidth: 120,
            flex: 1,
        },
        {
            field: 'Longitud',
            headerName: 'Longitud',
            minWidth: 120,
            flex: 1,
        },
        {
            field: 'Usuario',
            headerName: 'Usuario',
            minWidth: 120,
            flex: 1,
        },
    ];

    const loadData = () => {
        if (formik.values.userId.length > 0) {
            asyncPostDataActivityTimesExport(formik.values).then(
                (body: any) => {
                    if (body.items !== undefined) {
                        setRows(body.items);
                        setLengthRows(body.items.length);
                    }
                }
            );
        }
    };

    useEffect(() => {
        asyncGetUserByRol('all').then((body: any) => {
            if (body.items !== undefined) {
                setUsers(body.items.items);
            }
        });
    }, []);

    const validateStartTime = (value: any) => {
        setStartTime(value);
        formik.values.startTime = value;
    };

    return (
        <>
            <ListView
                title={titleList}
                titleButtonAdd={'Descargar'}
                onClickAdd={formik.handleSubmit}
                startIconButtonAdd={<FileDownloadIcon color="secondary" />}
                onClickBack={() => navigate(HOME)}
                rows={rows}
                columns={columns}
                enabledButtonGeneral={true}
                titleButtonGeneral="cargar info"
                onClickButtonGeneral={loadData}
                rowCount={lengthRows}
                onSetPage={(paginationModel: any) => paginator(paginationModel)}
                enabledSearch={true}
                searchField={
                    <form className="form">
                        <Grid
                            container
                            spacing={{ xs: 1.5, md: 1.5 }}
                            columnSpacing={{ xs: 1.5, sm: 1.5, md: 1.5 }}
                            sx={{ minWidth: 300 }}
                        >
                            <Grid item xs={12} sm={6} md={4} lg={4}>
                                <DatePickerField
                                    label="Fecha inicio"
                                    name="startTime"
                                    size="small"
                                    required={true}
                                    maxDate={dayjs()}
                                    touched={formik.touched.startTime}
                                    errors={formik.errors.startTime?.toString()}
                                    value={formik.values.startTime}
                                    onChange={(value) =>
                                        validateStartTime(value)
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4} lg={4}>
                                <DatePickerField
                                    label="Fecha fin"
                                    name="endTime"
                                    size="small"
                                    required={Boolean(startTime)}
                                    minDate={dayjs(startTime)}
                                    maxDate={dayjs()}
                                    disabled={!startTime}
                                    touched={formik.touched.endTime}
                                    errors={formik.errors.endTime?.toString()}
                                    value={formik.values.endTime}
                                    onChange={(value) =>
                                        formik.setFieldValue(
                                            'endTime',
                                            value,
                                            true
                                        )
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} sm={12} md={4} lg={4}>
                                <MultipleAutocompleteField
                                    data={users}
                                    label="Ingrese el nombre del agente a buscar"
                                    name="userId"
                                    size="small"
                                    touched={formik.touched.userId}
                                    errors={formik.errors.userId}
                                    value={formik.values.userId}
                                    onChangeValue={(value) =>
                                        formik.setFieldValue('userId', value)
                                    }
                                    getOptionLabel={(option) =>
                                        option
                                            ? option.name + ' ' + option.surname
                                            : ''
                                    }
                                />
                            </Grid>
                        </Grid>
                    </form>
                }
            />
        </>
    );
};

export default ExportActivityTimes;
